import React, { useState, useRef, useEffect } from "react";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import InputLabel from "components/InputLabel";
import clsx from "clsx";
import { PopperPlacementType } from "@material-ui/core";
import { IconCalendar } from "@tabler/icons-react";
import { de, enUS } from "date-fns/locale";
import { globalStore } from "store/GlobalStore";

type Props = {
  label: string;
  value?: string;
  containerClassName?: string;
  inputClassName?: string;
  dateFormat?: string;
  placeholder?: string;
  name?: string;
  monthYearPicker?: boolean;
  yearPicker?: boolean;
  minDate?: string;
  maxDate?: string;
  popperPlacement?: "top" | "top-start" | "bottom" | "auto";
  onChange?: (value: string | undefined | Date, name: string) => void;
  onChangeDgnb?: (value: string, name: string) => void;
  isDgnb?: boolean;
  height?: "large" | "middle" | "small";
  required?: boolean;
};

const CustomDatePicker: React.FC<Props> = ({
  label,
  value,
  dateFormat,
  containerClassName,
  inputClassName,
  onChange,
  onChangeDgnb,
  placeholder,
  monthYearPicker = false,
  yearPicker,
  minDate,
  maxDate,
  name = "",
  isDgnb,
  popperPlacement = "bottom",
  required,
  ...props
}) => {
  const [date, setDate] = useState<Date | undefined>();
  const [inputValue, setInputValue] = useState<string>();

  globalStore.currentLanguage === "de"
    ? registerLocale("de", de)
    : registerLocale("enUS", enUS);

  useEffect(() => {
    !date &&
      setDate(moment(value).isValid() ? moment(value).toDate() : undefined);
  }, [value]);

  useEffect(() => {
    if (date) {
      setInputValue(moment(date).format(getFormat()));
    } else {
      setInputValue("");
    }
  }, [date, name]);

  const handleOnChange = (date: Date | null) => {
    if (!date) return;
    setDate(date);
    if (isDgnb) {
      const dateValue = moment(date).isValid()
        ? moment(date).format(getFormat())
        : "";
      onChangeDgnb && onChangeDgnb(dateValue, name);
    } else {
      onChange && onChange(date, name);
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const datePickerRef = useRef<ReactDatePicker>(null);

  const handleIconClick = () => {
    inputRef.current?.focus();
  };

  function getFormat() {
    if (dateFormat && monthYearPicker) {
      return "MM.YYYY";
    }
    return dateFormat && yearPicker ? "YYYY" : "DD.MM.YYYY";
  }

  const validateDateValue = () => {
    if (isDgnb && value?.includes("GMT+")) {
      return moment(value).format(getFormat());
    }
    if (value && isDgnb) {
      return value;
    }
    if (moment(value).isValid()) {
      return moment(value).format(getFormat());
    }
    return "";
  };

  function getFormatConfig() {
    const fmt = getFormat();

    if (fmt === "MM.YYYY") {
      return {
        maxLength: 7,
        allowedChars: /^[0-9.]*$/,
        pattern: /^(\d{0,2})(\.?)(\d{0,4})$/, // MM.YYYY
      };
    }

    if (fmt === "YYYY") {
      return {
        maxLength: 4,
        allowedChars: /^[0-9]*$/,
        pattern: /^\d{0,4}$/, // YYYY
      };
    }

    return {
      maxLength: 10,
      allowedChars: /^[0-9.]*$/,
      pattern: /^(\d{0,2})(\.?)(\d{0,2})(\.?)(\d{0,4})$/, // DD.MM.YYYY
    };
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    const { maxLength, allowedChars, pattern } = getFormatConfig();
    datePickerRef.current?.setOpen(false);

    if (!allowedChars.test(next) || next.length > maxLength) return;
    if (!pattern.test(next)) return;

    setInputValue(next);

    if (next.length === maxLength) {
      const m = moment(next, getFormat(), true);
      if (m.isValid()) {
        const newDate = m.toDate();
        if (newDate.getTime() !== date?.getTime()) {
          setDate(newDate);
        }
      }
    }
  };

  const handleInputBlur = () => {
    if (!inputValue) {
      setDate(undefined);
      setInputValue("");
      return;
    }

    const m = moment(inputValue, getFormat(), true);
    if (!m.isValid()) {
      setInputValue(validateDateValue());
    }
  };

  return (
    <div className={`flex flex-col w-full ${containerClassName}`}>
      <InputLabel
        required={required}
        labelName={label}
        name={label?.replaceAll(" ", "_")}
      />
      <ReactDatePicker
        locale={globalStore.currentLanguage === "de" ? "de" : "enUS"}
        popperPlacement={popperPlacement as PopperPlacementType}
        selected={date}
        name={name}
        onChange={handleOnChange}
        dateFormat={dateFormat}
        showMonthYearPicker={monthYearPicker}
        showYearPicker={yearPicker}
        ref={datePickerRef}
        minDate={minDate ? moment(minDate).toDate() : undefined}
        maxDate={maxDate ? moment(maxDate).toDate() : undefined}
        customInput={
          <div className="flex items-center">
            <input
              ref={inputRef}
              type="text"
              className={clsx(
                "w-full py-2 pr-10 pl-3 placeholder:text-gray-500 text-gray-700 bg-white border",
                "border-gray-300 focus:ring-0 focus:border-indigo-500 rounded-md focus:outline-none text-sm",
                {
                  "h-large": props.height === "large",
                  "h-middle": props.height === "middle",
                  "h-small": props.height === "small",
                },
                inputClassName
              )}
              placeholder={placeholder}
              value={inputValue ?? validateDateValue()}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 rounded-r-md"
              onClick={handleIconClick}
            >
              <IconCalendar className="h-5 w-5" />
            </button>
          </div>
        }
      />
    </div>
  );
};

export default CustomDatePicker;
